import React from 'react';
import { AppBar, Box, Divider, Stack, SxProps } from '@mui/material';
import { useLocation } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import AppBarControlPanel, {
  AppBarControlPanelProps,
} from './AppBarControlPanel';
import AppToolbar from './AppToolbar';
import { navRailWidth } from './NavRailMenuList';
import { getSurfaceOpacityByElevation } from './M3/M3SurfaceContainer';
import { M3IconButton } from './M3/M3Button';
import ProfileControl from './Profile/ProfileControl';
import WebAppSettings from './Settings/WebAppSettings/WebAppSettings';
import { useModal } from './M3/M3Modal';

import { useAppProvider } from '../providers/app/app';
import { MenuItem } from '../types/types';
import { applyOpacityOnColor } from '../utils/color';

type Props = {
  children?: any;
  noAppDrawer?: boolean;
};

const menuIconButtonSx: SxProps = {};

function AppNavigationBar({ children, noAppDrawer }: Props) {
  const { pathname } = useLocation();
  const {
    toolbarHeight,
    palettes: { main: palette },
    isDarkMode,
    setDarkMode,
    updateActionKeyCounter,
    setIsDrawerOpen,
  } = useAppProvider();

  const webAppSettingsModal = useModal();

  const standaloneMenus: (MenuItem & Partial<AppBarControlPanelProps>)[] = [
    {
      id: 'dark-mode-switch',
      navigate: false,
      name: isDarkMode ? 'Light Mode' : 'Dark Mode',
      icon: isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />,
      onClick: () => {
        setDarkMode(!isDarkMode);
        updateActionKeyCounter('toggle_switch_dark_mode');
      },
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: <SettingsOutlinedIcon />,
      active: webAppSettingsModal.isOpen,
      onClick: () => webAppSettingsModal.open(),
    },
  ];

  return (
    <>
      <AppBar
        elevation={0}
        position='fixed'
        sx={{
          background: isDarkMode
            ? 'var(--md-sys-color-background-dark)'
            : 'var(--md-sys-color-background-light)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <AppToolbar
          style={{
            paddingLeft: 0,
            background: applyOpacityOnColor(
              isDarkMode
                ? palette['md.ref.palette.primary80']
                : palette['md.ref.palette.primary40'],
              getSurfaceOpacityByElevation(2),
            ),
          }}
        >
          <Box flex={1} display='flex' alignItems='center'>
            <Box
              sx={{
                height: toolbarHeight,
                width: navRailWidth,
                minWidth: navRailWidth,
                background: noAppDrawer
                  ? undefined
                  : isDarkMode
                  ? 'var(--md-sys-color-background-dark)'
                  : 'var(--md-sys-color-background-light)',
                mr: 3,
              }}
            >
              <Box
                display='flex'
                alignItems='center'
                justifyContent='center'
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: noAppDrawer
                    ? undefined
                    : applyOpacityOnColor(
                        isDarkMode
                          ? palette['md.ref.palette.primary80']
                          : palette['md.ref.palette.primary40'],
                        getSurfaceOpacityByElevation(5),
                      ),
                }}
              >
                {/* <img
                src='/icon-128.png'
                width={36}
                height={36}
                style={{
                  borderRadius: 4,
                  display: 'block',
                }}
              /> */}
                <M3IconButton onClick={() => setIsDrawerOpen(true)}>
                  <MenuOutlinedIcon />
                </M3IconButton>
              </Box>
            </Box>
          </Box>
          <Stack
            gap={1}
            direction='row'
            justifyContent='flex-end'
            alignItems='center'
          >
            {standaloneMenus.map(
              (menu: MenuItem & Partial<AppBarControlPanelProps>) => {
                return (
                  <AppBarControlPanel
                    key={menu.id}
                    tooltip={{
                      title: menu.name as string,
                      placement: 'bottom',
                    }}
                    active={menu.active || pathname.includes(menu.basePath!)}
                    redirectTo={menu.path}
                    icon={menu.icon}
                    onClick={menu.onClick}
                    iconButtonSx={menuIconButtonSx}
                    withPopover={menu.withPopover}
                    menus={menu.menus}
                  />
                );
              },
            )}
            <Divider orientation='vertical' flexItem sx={{ ml: 1, mr: 2 }} />
            <ProfileControl />
          </Stack>
          {/* <M3Button
            sx={{
              pl: 2,
              pr: 2,
            }}
            onClick={logout}
            data-testid='logout-button'
          >
            <LogoutIcon sx={{ fontSize: 22 }} />
            Log out
          </M3Button> */}
        </AppToolbar>
      </AppBar>
      <AppToolbar />
      <WebAppSettings modal={webAppSettingsModal} />
    </>
  );
}

export default AppNavigationBar;
